<template>
	<div class="page">
		<div class="tabs-container">
			<v-tabs
				v-model="activeTab"
				height="auto"
			>
				<v-tab data-test="tasks-tab">Tasks</v-tab>
				<v-tab data-test="builds-tab">Builds</v-tab>
			</v-tabs>
		</div>

		<v-window v-model="activeTab">
			<!-- Tasks Tab -->
			<v-window-item>
				<div class="content-sections">
					<TaskListDisplay
						data-test="active-tasks-list"
						sectionTitle="Active Tasks"
						sectionDescription="Tasks with status 'Accepted', 'Compiling', 'Building', or 'In Progress' will show up here."
						tasksLoadingMessage="Loading Active Tasks"
						noTasksMessage="No active tasks."
						:taskList="tasksStore.activeTasks"
						:taskUpdateStatus="tasksStore.activeTaskUpdateStatus"
						:canLoadMore="false"
						:dataLoaded="tasksStore.activeTasks !== undefined"
						@retry="retryActiveTasks"
					/>
					<TaskListDisplay
						data-test="inactive-tasks-list"
						sectionTitle="Completed Tasks"
						sectionDescription="Tasks with status 'Completed', 'Cancelled', or 'Stopped' will show up here."
						tasksLoadingMessage="Loading Completed Tasks"
						noTasksMessage="No completed tasks found"
						:taskList="tasksStore.inactiveTasks"
						:taskUpdateStatus="tasksStore.taskHistoryUpdateStatus"
						:continuationKey="tasksStore.taskListContinuationKey"
						:canLoadMore="true"
						:dataLoaded="tasksStore.taskHistory !== undefined"
						@load-more-tasks="loadMoreTasks"
						@retry="retryInactiveTasks"
					/>
				</div>
			</v-window-item>

			<!-- Builds Tab -->
			<v-window-item>
				<div class="content-sections">
					<BuildListDisplay
						data-test="active-builds-list"
						sectionTitle="Active Builds"
						sectionDescription="Builds with status 'Accepted', 'Initialising', or 'In Progress' will show up here."
						buildsLoadingMessage="Loading Active Builds"
						noBuildsMessage="No active builds."
						:buildList="sortedActiveBuilds"
						:buildUpdateStatus="buildsStore.activeBuildUpdateStatus"
						:canLoadMore="false"
						:dataLoaded="buildsStore.activeBuilds !== undefined"
						keyPrefix="active"
						@retry="retryActiveBuilds"
					/>
					<BuildListDisplay
						data-test="inactive-builds-list"
						sectionTitle="Completed Builds"
						sectionDescription="Builds with status 'Completed', 'Cancelled', 'Stopped', or 'Error' will show up here."
						buildsLoadingMessage="Loading Completed Builds"
						noBuildsMessage="No completed builds found"
						:buildList="inactiveBuilds"
						:buildUpdateStatus="buildsStore.buildHistoryUpdateStatus"
						:continuationKey="buildsStore.buildListContinuationKey"
						:canLoadMore="true"
						:dataLoaded="buildsStore.buildHistory !== undefined"
						keyPrefix="inactive"
						@load-more-builds="loadMoreBuilds"
						@retry="retryInactiveBuilds"
					/>
				</div>
			</v-window-item>
		</v-window>
	</div>
</template>

<script lang="ts">
import { defineComponent } from "vue";

// Types
import { UserLimitsE } from "@/js/tierconfig";
import { Build, isBuildActive } from "@/types/api/builds";

// Libraries

// Components
import TaskListDisplay from "@/components/TaskManager/TaskListDisplay.vue";
import BuildListDisplay from "@/components/TaskManager/BuildListDisplay.vue";

// Carbon icons
import Information16 from "@carbon/icons-vue/es/information/16";

// Stores
import { useTasksStore } from "@/stores/tasks";
import { useBuildsStore } from "@/stores/builds";

// Global variables

// Local Types

export default defineComponent({
	name: "TaskManager",
	// eslint-disable-next-line vue/no-unused-components
	components: { TaskListDisplay, BuildListDisplay, Information16 },
	data() {
		return {
			activeTab: 0,
		};
	},
	setup() {
		const tasksStore = useTasksStore();
		const buildsStore = useBuildsStore();
		return { tasksStore, buildsStore, UserLimitsE };
	},
	computed: {
		sortedActiveBuilds(): Build[] {
			if (!this.buildsStore.activeBuilds) return [];
			// Deduplicate by BuildID, then sort by CreatedAt descending (latest first)
			const uniqueBuilds = this.deduplicateBuilds(this.buildsStore.activeBuilds);
			return uniqueBuilds.sort((a, b) => b.CreatedAt - a.CreatedAt);
		},
		inactiveBuilds(): Build[] {
			if (!this.buildsStore.buildHistory) return [];
			// Filter inactive builds, deduplicate, and sort by CreatedAt descending (latest first)
			const inactive = this.buildsStore.buildHistory.filter((build) => !isBuildActive(build.Status));
			const uniqueBuilds = this.deduplicateBuilds(inactive);
			return uniqueBuilds.sort((a, b) => b.CreatedAt - a.CreatedAt);
		},
	},
	methods: {
		deduplicateBuilds(builds: Build[]): Build[] {
			// Create a Map to deduplicate by BuildID, keeping the first occurrence
			const buildMap = new Map<string, Build>();
			builds.forEach((build) => {
				if (!buildMap.has(build.BuildID)) {
					buildMap.set(build.BuildID, build);
				}
			});
			return Array.from(buildMap.values());
		},
		async loadMoreTasks() {
			await this.tasksStore.fetchNextTaskBatchFromRemote();
		},
		async loadMoreActiveTasks() {
			// Currently not being used
			await this.tasksStore.fetchNextActiveTaskBatchFromRemote();
		},
		async loadMoreBuilds() {
			await this.buildsStore.fetchNextBuildBatchFromRemote();
		},
		async retryActiveTasks() {
			await this.tasksStore.fetchActiveTasksFromRemote();
		},
		async retryInactiveTasks() {
			await this.tasksStore.fetchNextTaskBatchFromRemote();
		},
		async retryActiveBuilds() {
			await this.buildsStore.fetchActiveBuildsFromRemote();
		},
		async retryInactiveBuilds() {
			await this.buildsStore.fetchNextBuildBatchFromRemote();
		},
	},
	async mounted() {
		// Initialize builds when component mounts
		if (this.buildsStore.buildHistory === undefined) {
			await this.buildsStore.initialiseBuildHistory();
		}
	},
});
</script>

<style lang="scss" scoped>
.card-list {
	display: flex;
	flex-direction: column;
	gap: var(--sgnl-space-12);
}
</style>
